/**
 * Professional-course deep-dive content for /careers/courses.
 *
 * Six India-focused credential guides (ACCA, ACCA-in-India, CMA-USA, CMA-India,
 * CA, US CPA), authored by the `research-pro-courses` workflow and fact-checked
 * for salary / cost / recognition accuracy. Pure information — no quizzes.
 * Data lives in proCourses.data.json; this module adds the types + helpers.
 */
import data from './proCourses.data.json';

export interface CourseVideo {
  youtubeId: string;
  title: string;
  durationSeconds: number;
}

/** One stage on the salary-progression chart (₹ lakh per annum, India). */
export interface SalaryPoint {
  label: string;
  low: number;
  high: number;
}

export interface ProCourse {
  slug: string;
  name: string;
  fullName: string;
  awardingBody: string;
  oneLiner: string;
  overview: string;
  eligibility: string;
  duration: string;
  structure: string;
  costInr: string;
  marketDemand: string;
  whereYouWork: string[];
  startingSalaryIndia: string;
  experiencedSalaryIndia: string;
  salaryAbroad: string;
  careerGrowth: string;
  indiaRecognition: string;
  pros: string[];
  cons: string[];
  whoShouldDoIt: string;
  sources?: string[];
  /** Fresher → mid → senior pay in India (₹ LPA), for the on-page chart. */
  salaryProgression?: SalaryPoint[];
  /** Set once the explainer video is rendered + uploaded. */
  video?: CourseVideo | null;
}

export const proCourses: ProCourse[] = data.courses as ProCourse[];

export const getCourse = (slug: string): ProCourse | undefined =>
  proCourses.find((c) => c.slug === slug);

/** First sentence of a prose field — for compact pills/labels. */
export const firstSentence = (s: string): string => (s.split('. ')[0] ?? s).replace(/\.$/, '');

/** Pull a compact "₹X-Y LPA" range out of a salary paragraph; else first sentence. */
export const shortSalary = (s: string): string => {
  const m = s.match(/(?:₹|Rs\.?\s?)\s*\d[\d.,]*\s*[-–]\s*\d[\d.,]*\s*LPA/i);
  return m && m[0] ? m[0].replace(/\s+/g, ' ').trim() : firstSentence(s);
};
